import { Component, EventEmitter, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import { AbstractFormDetalleTicket } from '../abstract-form-detalle-ticket';
import { Bonocliente } from '../../../models/bonocliente';
import { RegisterTicket } from '../../../models/ticket';

@Component({
  selector: 'app-form-ticket',
  templateUrl: './form-ticket.component.html',
  styleUrls: ['./form-ticket.component.scss']
})
export class FormTicketComponent extends AbstractFormDetalleTicket {

  @Output() save = new EventEmitter<RegisterTicket>();

  protected configurarComponenteEspecifico(): void {
    this.suscribirCambiosParaRecalculo();
  }

  onSubmit(): void {
    if (this.tForm.valid) {
      // Asegurar que los totales estén actualizados antes de enviar
      this.calcularImporteTotal();

      if (!this.validarTotales()) {
        this.sendToastMsg.emit({
          mensajeError: true,
          mensaje: 'El total a pagar no coincide con las formas de pago'
        });
        return;
      }

      const payload = this.generarPayloadTicket();
      this.save.emit(payload);
    }
  }

  onBcEconomicoSeleccionado(): void {
    const bonoclienteId = this.tForm.get('bonoEconomicoId')?.value;
    const bonocliente = this.bcEconomicos.find(b => b.id === bonoclienteId);

    if (bonocliente) {
      const totalSinDescuento = this.calcularTotalServiciosSinDescuento();
      this.descuentoEconomico = Math.min(totalSinDescuento, bonocliente.saldo);
      this.calcularImporteTotal();
    } else {
      this.descuentoEconomico = 0;
      this.calcularImporteTotal();
    }
  }

  calcularTotalServiciosSinDescuento(): number {
    return this.eventServicioDetalles.controls.reduce((acc, grupo) => {
      const precio = grupo.value.precio;
      const cantidad = this.cantidadDelServicio(grupo.value.nombre);
      return acc + precio * cantidad;
    }, 0);
  }

  private generarPayloadTicket(): RegisterTicket {
    const servicios = this.eventServicioDetalles.controls.map(grupo => {
      const servicioId = grupo.get('servicioId')?.value;
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);

      const precioOriginal = Number(grupo.get('precio')?.value ?? 0);
      const financiado = !!grupo.get('financiado')?.value;
      const numPlazos = financiado ? Number(grupo.get('numPlazos')?.value ?? 0) : undefined;
      const precioPlazo = financiado ? Number(grupo.get('precioPlazo')?.value ?? 0) : undefined;

      const bonoclienteId = grupo.get('bonoclienteId')?.value || undefined;
      const descuentoBono = Number(grupo.get('descuentoBono')?.value ?? 0);
      const userId = grupo.get('userId')?.value || undefined;

      return {
        servicioId,
        cantidad,
        precioUnitario: precioOriginal,
        precioCobrado: financiado ? precioPlazo || precioOriginal : precioOriginal,
        financiado,
        numPlazos,
        precioPlazo,
        bonoclienteId,
        descuentoBono,
        sesionesGastadas: bonoclienteId ? 1 : undefined,
        userId
      };
    });

    const productos = this.eventProductoDetalles.controls.map(grupo => {
      const productoId = grupo.get('productoId')?.value;
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precioUnitario = Number(grupo.get('precio')?.value ?? 0);
      const userId = grupo.get('userId')?.value;
      return {
        productoId,
        cantidad,
        precioUnitario,
        userId
      };
    });

    // Separar bonos de sesiones y bonos económicos según el DTO esperado
    const bonosAplicados: any[] = [];

    // Bonos de sesiones (de servicios)
    servicios.forEach(s => {
      if (s.bonoclienteId && s.sesionesGastadas) {
        bonosAplicados.push({
          bonoclienteId: s.bonoclienteId,
          sesionesGastadas: s.sesionesGastadas,
          eurosGastados: undefined
        });
      }
    });

    // Bonos económicos
    this.bonosEconomicos.controls.forEach(grupo => {
      const id = grupo.get('bonoclienteId')?.value;
      const eurosGastados = Number(grupo.get('importeADescontar')?.value ?? 0);
      if (id && eurosGastados > 0) {
        bonosAplicados.push({
          bonoclienteId: id,
          eurosGastados,
          sesionesGastadas: undefined
        });
      }
    });

    return {
      id: this.ticketId || undefined,
      eventId: this.cita.id,
      clienteId: this.clienteId,
      tiendaId: this.tiendaId,
      userId: this.userId,
      servicios,
      productos,
      bonosAplicados,
      total: this.tForm.get('total')?.value,
      totalPagar: this.totalPagar,
      importeFinanciado: this.importeFinanciado,
      totalIva: this.tForm.get('totalIva')?.value,
      totalSinIva: this.tForm.get('totalSinIva')?.value,
      ivaAplicado: (this.tForm.get('ivaAplicado')?.value ?? 0) / 100,
      // Campos de pago
      conEfectivo: this.tForm.get('conEfectivo')?.value || false,
      conTarjeta: this.tForm.get('conTarjeta')?.value || false,
      conOtros: this.tForm.get('conOtros')?.value || false,
      importeEfectivo: this.tForm.get('conEfectivo')?.value ?
        Number(this.tForm.get('importeEfectivo')?.value ?? 0) : 0,
      importeTarjeta: this.tForm.get('conTarjeta')?.value ?
        Number(this.tForm.get('importeTarjeta')?.value ?? 0) : 0,
      importeOtros: this.tForm.get('conOtros')?.value ?
        Number(this.tForm.get('importeOtros')?.value ?? 0) : 0,
      efectivoEntregado: this.tForm.get('conEfectivo')?.value ?
        Number(this.tForm.get('efectivoEntregado')?.value ?? 0) : 0,
      cambioDevuelto: this.tForm.get('conEfectivo')?.value ?
        Number(this.tForm.get('cambioDevuelto')?.value ?? 0) : 0
    };
  }

  validarTotales(): boolean {
    // Obtener los importes de las diferentes formas de pago
    const importeEfectivo = this.tForm.get('conEfectivo')?.value ?
      Number(this.tForm.get('importeEfectivo')?.value ?? 0) : 0;

    const importeTarjeta = this.tForm.get('conTarjeta')?.value ?
      Number(this.tForm.get('importeTarjeta')?.value ?? 0) : 0;

    const importeOtros = this.tForm.get('conOtros')?.value ?
      Number(this.tForm.get('importeOtros')?.value ?? 0) : 0;

    const sumaPagos = importeEfectivo + importeTarjeta + importeOtros;

    return Math.abs(this.totalPagar - sumaPagos) < 0.01;
  }

  agregarBonoEconomico(bono: Bonocliente): void {
    if (!bono) return;

    // Verificar si ya existe
    const yaExiste = this.bonosEconomicos.controls.some(
      g => g.get('bonoclienteId')?.value === bono.id
    );
    if (yaExiste) return;

    // Calcular total base (servicios + productos - bonos de sesiones)
    let totalBase = 0;
    this.eventServicioDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      const descuentoBono = Number(grupo.get('descuentoBono')?.value ?? 0);
      totalBase += (precio * cantidad) - descuentoBono;
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      totalBase += precio * cantidad;
    });

    // Calcular cuánto ya se ha descontado con otros bonos económicos
    const importeYaDescontado = this.bonosEconomicos.controls.reduce((acc, g) => {
      return acc + Number(g.get('importeADescontar')?.value ?? 0);
    }, 0);

    // El restante es lo que queda disponible para este bono
    const restante = Math.max(totalBase - importeYaDescontado, 0);

    // El importe a aplicar es el mínimo entre el saldo del bono y lo que queda por descontar
    const importe = Math.min(restante, bono.saldo);

    const grupo = this.fb.group({
      bonoclienteId: [bono.id],
      importeADescontar: [importe, [
        Validators.min(0),
        Validators.max(bono.saldo)
      ]]
    });

    // IMPORTANTE: Suscribirse DESPUÉS de agregar al FormArray
    this.bonosEconomicos.push(grupo);

    grupo.get('importeADescontar')?.valueChanges.subscribe(() => {
      this.calcularImporteTotal();
      // this.actualizarValidadoresBonosEconomicos(); // Nuevo método
    });

    this.calcularImporteTotal();
    // this.actualizarValidadoresBonosEconomicos(); // Actualizar validadores de todos los bonos
  }

  eliminarBonoEconomico(index: number): void {
    this.bonosEconomicos.removeAt(index);
    this.calcularImporteTotal();
  }

  /**
   * Ajusta los bonos económicos si sobrepasan el total disponible para pagar
   */
  private ajustarBonosEconomicosExcedidos(): void {
    if (this.bonosEconomicos.length === 0) return;

    // Calcular cuánto hay en bonos económicos actualmente
    const totalDescuentoBonos = this.bonosEconomicos.controls.reduce((acc, g) => {
      return acc + Number(g.get('importeADescontar')?.value ?? 0);
    }, 0);

    // Calcular el total BRUTO (servicios + productos) sin descuentos de bonos de sesiones ni económicos
    let totalBruto = 0;

    this.eventServicioDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      totalBruto += precio * cantidad;
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      totalBruto += precio * cantidad;
    });

    // Calcular descuentos por bonos de sesiones
    let descuentosSesiones = 0;
    this.eventServicioDetalles.controls.forEach(grupo => {
      const descuentoBono = Number(grupo.get('descuentoBono')?.value ?? 0);
      descuentosSesiones += descuentoBono;
    });

    // El total disponible para bonos económicos es: total bruto - descuentos de sesiones
    const totalDisponibleParaBonosEconomicos = totalBruto - descuentosSesiones;

    // Si los bonos económicos NO exceden lo disponible, OK
    if (totalDescuentoBonos <= totalDisponibleParaBonosEconomicos) {
      return;
    }

    // Si excede, redistribuir: solo podemos aplicar hasta totalDisponibleParaBonosEconomicos
    let restante = totalDisponibleParaBonosEconomicos;

    this.bonosEconomicos.controls.forEach((grupo, index) => {
      const bonoId = grupo.get('bonoclienteId')?.value;
      const saldo = this.getSaldoBono(bonoId);
      const importeActual = Number(grupo.get('importeADescontar')?.value ?? 0);

      // Ajustar: lo mínimo entre el saldo del bono y lo que queda disponible
      const importeAjustado = Math.min(saldo, Math.max(0, restante));


      // Solo actualizar si cambió
      if (importeActual !== importeAjustado) {
        grupo.get('importeADescontar')?.setValue(importeAjustado, { emitEvent: false });
      }

      restante -= importeAjustado;
    });

    // Recalcular el total después del ajuste
    this.calcularImporteTotal();
  }

  getImporteMaximoParaBonoEconomico(index: number): number {
    const grupo = this.bonosEconomicos.at(index);
    const bonoId = grupo.get('bonoclienteId')?.value;
    const saldo = this.getSaldoBono(bonoId);

    // Calcular total base (servicios + productos - bonos de sesiones)
    let totalBase = 0;
    this.eventServicioDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      const descuentoBono = Number(grupo.get('descuentoBono')?.value ?? 0);
      totalBase += (precio * cantidad) - descuentoBono;
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      const cantidad = Number(grupo.get('cantidad')?.value ?? 1);
      const precio = Number(grupo.get('precio')?.value ?? 0);
      totalBase += precio * cantidad;
    });

    // Ya descontado por otros bonos económicos (excluyendo el actual)
    const importeYaDescontado = this.bonosEconomicos.controls.reduce((acc, g, i) => {
      if (i === index) return acc;
      return acc + Number(g.get('importeADescontar')?.value ?? 0);
    }, 0);

    const disponible = Math.max(0, totalBase - importeYaDescontado);
    return Math.min(saldo, disponible);
  }

  private suscribirCambiosParaRecalculo(): void {
    this.eventServicioDetalles.controls.forEach(grupo => {
      grupo.valueChanges.subscribe(() => {
        this.calcularImporteTotal();
        this.ajustarBonosEconomicosExcedidos();
      });
    });

    this.eventProductoDetalles.controls.forEach(grupo => {
      grupo.valueChanges.subscribe(() => {
        this.calcularImporteTotal();
        this.ajustarBonosEconomicosExcedidos();
      });
    });

    this.tForm.get('importeADescontar')?.valueChanges.subscribe(() => {
      this.calcularImporteTotal();
    });

    this.tForm.get('eventServicioDetalles')?.valueChanges.subscribe(() => {
      this.calcularImporteTotal();
      this.ajustarBonosEconomicosExcedidos();
    });

    this.tForm.get('ivaAplicado')?.valueChanges.subscribe(() => {
      this.calcularImporteTotal();
    });
  }

}
